import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { MARKS, cycle, isScript, nextType, typeOf, TYPES } from "@/lib/script";

export const scriptKey = new PluginKey("script");

const paragraphs = (doc) => {
  const out = [];
  doc.forEach((node, pos) => { if (node.type.name === "paragraph") out.push({ node, pos, type: typeOf(node.textContent) }); });
  return out;
};

const build = (doc, opts) => {
  if (!isScript(opts.mode)) return DecorationSet.empty;
  const allowed = new Set(TYPES[opts.mode].map((t) => t[0]));
  return DecorationSet.create(doc, paragraphs(doc).filter((p) => p.type).map((p) =>
    Decoration.node(p.pos, p.pos + p.node.nodeSize, { class: `sc-line sc-${allowed.has(p.type) ? p.type : "action"}` })));
};

export const currentType = (state) => {
  const { $from } = state.selection;
  return $from.parent.type.name === "paragraph" ? typeOf($from.parent.textContent) : null;
};

export const setLineType = (editor, type) => {
  const { state } = editor;
  const { $from } = state.selection;
  if ($from.parent.type.name !== "paragraph") return false;
  const start = $from.start();
  const tr = state.tr;
  if (typeOf($from.parent.textContent)) tr.replaceWith(start, start + 1, state.schema.text(MARKS[type]));
  else tr.insertText(MARKS[type], start);
  editor.view.dispatch(tr);
  editor.view.focus();
  return true;
};

export const ScriptMarks = Extension.create({
  name: "scriptMarks",
  addOptions() { return { mode: "prose" }; },
  addKeyboardShortcuts() {
    const on = (ed) => isScript(scriptKey.getState(ed.state)?.opts.mode);
    const keys = {
      Tab: ({ editor }) => on(editor) && setLineType(editor, cycle(scriptKey.getState(editor.state).opts.mode, currentType(editor.state) || "action")),
      "Shift-Tab": ({ editor }) => on(editor) && setLineType(editor, cycle(scriptKey.getState(editor.state).opts.mode, currentType(editor.state) || "action", -1)),
    };
    const atStart = (ed) => { const { $from, empty } = ed.state.selection; return on(ed) && empty && $from.parent.type.name === "paragraph" && $from.parentOffset === 1 && typeOf($from.parent.textContent) ? $from : null; };
    keys.Backspace = ({ editor }) => {
      const $from = atStart(editor);
      if (!$from || $from.index($from.depth - 1) === 0) return !!$from;
      return editor.chain().command(({ tr }) => { tr.delete($from.pos - 1, $from.pos); return true; }).joinBackward().run();
    };
    keys.ArrowLeft = ({ editor }) => {
      const $from = atStart(editor);
      if (!$from) return false;
      if ($from.index($from.depth - 1) > 0) editor.view.dispatch(editor.state.tr.setSelection(TextSelection.near(editor.state.doc.resolve($from.before()), -1)));
      return true;
    };
    return keys;
  },
  addProseMirrorPlugins() {
    const start = { ...this.options };
    return [new Plugin({
      key: scriptKey,
      state: {
        init: (_, { doc }) => ({ opts: start, decos: build(doc, start) }),
        apply: (tr, prev) => {
          const meta = tr.getMeta(scriptKey);
          const opts = meta ? { ...prev.opts, ...meta } : prev.opts;
          return tr.docChanged || meta ? { opts, decos: build(tr.doc, opts) } : prev;
        },
      },
      appendTransaction: (trs, _old, state) => {
        const opts = scriptKey.getState(state).opts;
        if (!isScript(opts.mode)) return null;
        let tr = null;
        if (trs.some((t) => t.docChanged || t.getMeta(scriptKey))) {
          let prev = null;
          paragraphs(state.doc).forEach((p) => {
            if (!p.type) { tr = tr || state.tr; tr.insertText(MARKS[nextType(prev)], tr.mapping.map(p.pos + 1)); prev = nextType(prev); } else prev = p.type;
          });
        }
        const sel = tr ? tr.selection : state.selection;
        if (sel.empty && sel.$from.parent.type.name === "paragraph" && sel.$from.parentOffset === 0 && typeOf(sel.$from.parent.textContent)) {
          tr = tr || state.tr;
          tr.setSelection(TextSelection.create(tr.doc, sel.from + 1));
        }
        return tr;
      },
      props: { decorations(state) { return scriptKey.getState(state).decos; } },
    })];
  },
});

export const refreshScript = (editor, patch) => {
  if (!editor || editor.isDestroyed) return;
  editor.view.dispatch(editor.state.tr.setMeta(scriptKey, patch));
  editor.view.dom.classList.toggle("lf-script-screen", patch.mode === "screenplay");
  editor.view.dom.classList.toggle("lf-script-stage", patch.mode === "stage");
};
